import type { ReactNode } from "react";
import { cn } from "../../lib/cn";
import { Skeleton } from "../../atoms/Skeleton/Skeleton";
import { Table, TableBody, TableHeader } from "../Table/Table";
import { TableCell, TableHead, TableRow } from "../Table/TableRow";

export interface DataTableColumn<T> {
  id: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  headerClassName?: string;
  cellClassName?: string;
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  emptyState?: ReactNode;
  // Renders skeletonRowCount placeholder rows/cards in the same shape as real
  // data instead of rows/emptyState — pass this instead of `rows={data ?? []}`
  // while data is still in flight, so "loading" and "genuinely empty" read
  // differently. See docs/design-system.md's "Loading states" section.
  loading?: boolean;
  skeletonRowCount?: number;
  // Card view (below md:) promotes one column to the card's heading and,
  // optionally, one more pinned beside it (typically a status Badge) —
  // default heading is the first column, no meta. Both are left out of the
  // label/value list underneath so nothing repeats.
  titleColumnId?: string;
  metaColumnId?: string;
  className?: string;
}

// Renders as a table from md: up, and a stacked list of cards below it — one
// column/row definition, two layouts. See docs/design-system.md's
// "Mobile-first" section: a wide table forces two-axis scrolling to read a
// single value on a narrow screen; a card just reorders that same value
// vertically instead, which is the more standard mobile pattern.
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  emptyState,
  loading = false,
  skeletonRowCount = 5,
  titleColumnId,
  metaColumnId,
  className = "",
}: DataTableProps<T>) {
  if (!loading && rows.length === 0) {
    return emptyState ? <div className={className}>{emptyState}</div> : null;
  }

  const titleColumn = columns.find((column) => column.id === titleColumnId) ?? columns[0];
  const metaColumn = metaColumnId ? columns.find((column) => column.id === metaColumnId) : undefined;
  const bodyColumns = columns.filter((column) => column.id !== titleColumn.id && column.id !== metaColumn?.id);

  if (loading) {
    const skeletonKeys = Array.from({ length: skeletonRowCount }, (_, index) => index);
    return (
      <div className={className}>
        <div className="hidden md:block">
          <Table>
            <TableHeader>
              <TableRow>
                {columns.map((column) => (
                  <TableHead key={column.id} className={column.headerClassName}>
                    {column.header}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {skeletonKeys.map((key) => (
                <TableRow key={key}>
                  {columns.map((column) => (
                    <TableCell key={column.id} className={column.cellClassName}>
                      <Skeleton variant="text" className="w-full max-w-[80%]" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <ul className="flex flex-col gap-3 md:hidden">
          {skeletonKeys.map((key) => (
            <li key={key} className="rounded-md border border-border bg-surface-raised p-4">
              <div className="mb-2 flex items-start justify-between gap-3">
                <Skeleton variant="text" className="w-32" />
                {metaColumn && <Skeleton variant="text" className="w-14 shrink-0" />}
              </div>
              <div className="flex flex-col gap-1.5">
                {bodyColumns.slice(0, 3).map((column) => (
                  <Skeleton key={column.id} variant="text" className="w-full" />
                ))}
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="hidden md:block">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((column) => (
                <TableHead key={column.id} className={column.headerClassName}>
                  {column.header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={rowKey(row)}>
                {columns.map((column) => (
                  <TableCell key={column.id} className={column.cellClassName}>
                    {column.cell(row)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <ul className="flex flex-col gap-3 md:hidden">
        {rows.map((row) => (
          <li key={rowKey(row)} className="rounded-md border border-border bg-surface-raised p-4">
            <div className="mb-2 flex items-start justify-between gap-3">
              <div className="font-medium text-text">{titleColumn.cell(row)}</div>
              {metaColumn && <div className="shrink-0">{metaColumn.cell(row)}</div>}
            </div>
            <dl className="flex flex-col gap-1.5">
              {bodyColumns.map((column) => {
                const value = column.cell(row);
                if (value === null || value === undefined || value === "") return null;
                return (
                  <div key={column.id} className="flex items-baseline justify-between gap-3 text-sm">
                    <dt className="shrink-0 text-text-muted">{column.header}</dt>
                    <dd className={cn("text-right text-text", column.cellClassName)}>{value}</dd>
                  </div>
                );
              })}
            </dl>
          </li>
        ))}
      </ul>
    </div>
  );
}
